import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ISessionState } from './session-state';
import {
    ClearSessionAction,
    FetchCompanyAction,
    FetchCompanyFeaturesAction,
    FetchSkillSetsAction,
    FetchUserAction,
    FetchUserPermissionsAction,
    InitSessionAction,
    InitSessionCompleteAction,
    SetCompanyAction,
    SetCompanyFeaturesAction,
    SetSessionLocaleAction,
    SetSkillSetsAction,
    SetUserAction,
    SetUserPermissionsAction,
    UpdateUserAction,
    UpdateUserEmailAction,
    UpdateUserPhoneAction
} from './session-actions';
import {
    COMPANY_FEATURES_FETCH,
    SESSION_CLEAR,
    SESSION_COMPANY_FEATURES_FETCH,
    SESSION_COMPANY_FETCH,
    SESSION_COMPANY_SET,
    SESSION_INIT,
    SESSION_LOCALE_SET,
    SESSION_SKILLSETS_FETCH,
    SESSION_USER_FETCH,
    SESSION_USER_PERMISSIONS_FETCH,
    SESSION_USER_SET,
    USER_EMAIL_UPDATE,
    USER_PHONE_UPDATE,
    USER_UPDATE
} from '../action-types';
import { UserDataService } from '@core/data/api/user/user-data.service';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { Observable, concatMap, exhaustMap, filter, forkJoin, from, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs';
import { CompanyDataService } from '@core/data/api/company/company-data.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { isEmptyObj, isNullOrUndefined } from '@core/utils/object';
import { environment } from '@environments/environment';
import { localeUpdate as localUpdateEn } from '@core/translation/locale/moment-en';
import { localeUpdate as localUpdateEs } from '@core/translation/locale/moment-es';
import { localeUpdate as localUpdateFr } from '@core/translation/locale/moment-fr';
import { getCompany, getSession } from './session-selectors';
import { FetchLocationsAction } from '../locations/locations-actions';
import { MergePreferenceAction } from '../preference/preference-actions';
import { LocalStorageService } from '@core/local-storage/local-storage.service';
import { STORE_DATA } from '@core/local-storage/local-storage';
import { FetchBillingAction } from '../billing/billing-actions';
import { SkillSetsDataService } from '@core/data/api/skills/skill-sets-data.service';
import { BootIntercomAction } from '../intercom/intercom-actions';
import { ConnectionPermission } from '@core/models/connection/connection-permission';
import { PreferenceDataService } from '@core/data/api/preference/preference-data.service';

@Injectable()
export class SessionEffects {

    constructor(private store$: Store<ISessionState>,
                private userDataService: UserDataService,
                private companyDataService: CompanyDataService,
                private translateService: TranslateService,
                private localStorageService: LocalStorageService,
                private skillSetsDataService: SkillSetsDataService,
                private preferenceDataService: PreferenceDataService,
                private actions$: Actions) {
    }

    initSession$ = createEffect(() =>
        this.actions$.pipe(
            ofType<InitSessionAction>(SESSION_INIT),
            concatMap((action) => {

                let requests$: Observable<any>[] = [];

                requests$.push(this.userDataService.fetchUser());
                requests$.push(this.companyDataService.fetchCompany());


                return forkJoin(requests$).pipe(
                    exhaustMap(([userResponse, companyResponse]) => {
                        const company = companyResponse.company;
                        const user = userResponse.user;

                        let prefsRequests$: Observable<any>[] = [];

                        prefsRequests$.push(this.preferenceDataService.fetchUserPrefs(userResponse.user.id));
                        prefsRequests$.push(this.preferenceDataService.fetchCompanyPrefs(company.id));

                        return forkJoin(prefsRequests$).pipe(
                            exhaustMap(([userPrefs, companyPrefs]) => {
                                const prefs = {};

                                // for new users where there was no preference, it could be an empty object
                                // and we do not want to override with the local preference that would have been determined
                                // when the fetch was happening
                                if (
                                    userPrefs &&
                                    userPrefs.prefs &&
                                    !isEmptyObj(userPrefs.prefs)
                                ) {
                                    prefs['user'] = {
                                        [user.id]: userPrefs.prefs,
                                    };
                                }
    
                                if (
                                    companyPrefs &&
                                    companyPrefs.prefs &&
                                    !isEmptyObj(companyPrefs.prefs)
                                ) {
                                    prefs['company'] = {
                                        [company.id]: companyPrefs.prefs,
                                    };
                                }

                                return [
                                    new SetUserAction(userResponse.user),
                                    new FetchUserPermissionsAction(userResponse.user.id),
                                    new SetCompanyAction(company),
                                    new MergePreferenceAction(prefs),
                                    new FetchLocationsAction(),
                                    new FetchCompanyFeaturesAction(),
                                    new FetchBillingAction(),
                                    new FetchSkillSetsAction(),
                                    new BootIntercomAction(),
                                    new InitSessionCompleteAction({
                                        isLogin: action.payload?.isLogin,
                                        bypassRedirection: action.payload?.bypassRedirection
                                    })
                                ];
                            })
                        );
                    }),
                    ossCatchError(([errorActions, error]) =>  {
                        return from(errorActions);
                    })
                );
            })
            
        )
    );

    /**
     * Fetch user request from action.
     */
    fetchUser$ = createEffect(() => this.actions$.pipe(
        ofType<FetchUserAction>(SESSION_USER_FETCH),
        concatMap(() => {

            return this.userDataService.fetchUser()
                .pipe(
                    map((res: any) => {

                        return new SetUserAction(res.user);
                    }),
                    ossCatchError(([errorActions]) => from(errorActions))
                );
        })
        
    ));

    updateUser$ = createEffect(() => 
        this.actions$.pipe(
            ofType<UpdateUserAction>(USER_UPDATE),
            concatMap((action: UpdateUserAction) => {

                return this.userDataService
                    .updateUser(action.payload.id, action.payload.request)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                // Since the req is not returning the updated user, calling FetchUser to get the updated user
                                new FetchUserAction(),
                                (action as any).success(response),
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions.concat([
                            (action as any).error(error)
                        ])))
                    );
            })
        )
    );

    updateUserPhone$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateUserPhoneAction>(USER_PHONE_UPDATE),
            concatMap((action: any) => {

                const { id, phone, pin } = action.payload;

                return this.userDataService
                    .updateUserPhone(id, phone, pin)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                // Since the req is not returning the updated user, calling FetchUser to get the updated user
                                new FetchUserAction(),
                                action.success(response)
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions.concat([
                            (action as any).error(error)
                        ])))
                    );
            })
        )
    );

    updateEmailPhone$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateUserEmailAction>(USER_EMAIL_UPDATE),
            concatMap((action: any) => {

                const { id, email, pin } = action.payload;

                return this.userDataService
                    .updateUserEmail(id, email, pin)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                // Since the req is not returning the updated user, calling FetchUser to get the updated user
                                new FetchUserAction(),
                                action.success(response)
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions.concat([
                            (action as any).error(error)
                        ])))
                    );
            })
        )
    );

    setLocalStorageSession = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                SESSION_USER_SET,
                SESSION_COMPANY_SET
            ),
            withLatestFrom(this.store$.select(getSession)),
            tap(([_, session]) => {
                this.localStorageService.setItem(STORE_DATA, {
                    ...session
                });
            })
        );
    }, { dispatch: false });


    clearLocalStorageSession = createEffect(() =>
        this.actions$.pipe(
            ofType<ClearSessionAction>(SESSION_CLEAR),
            tap((_) => {
                this.localStorageService.removeItem(STORE_DATA);
            })
        )
    , { dispatch: false });

    /**
     * Fetch company request from action.
     */
    fetchCompany$ = createEffect(() => this.actions$.pipe(
        ofType<FetchCompanyAction>(SESSION_COMPANY_FETCH),
        concatMap(() => {

            return this.companyDataService.fetchCompany().pipe(
                map((res: any) => {

                    return new SetCompanyAction(res.company);
                }),
                ossCatchError(([errorActions]) => from(errorActions))
            );
        }),
        
    ));

    

    /**
     * Configure services with session based locale.
     */
    locale$ = createEffect(() => this.actions$.pipe(
        ofType<SetSessionLocaleAction>(SESSION_LOCALE_SET),
        map((action: SetSessionLocaleAction) => action.payload.locale),
        tap((locale: string) => {
            if (isNullOrUndefined(locale)) {
                locale = environment.settings.defaultLocale;
            }

            // Update all translations
            // If the lang isn't available, it will use the current loader to get them
            this.translateService.setDefaultLang(locale);
            this.translateService.use(locale);

            // Apply custom locale settings
            let localeUpdate;
            switch (locale.toLocaleLowerCase()) {
                case 'es-us':
                    localeUpdate = localUpdateEs();
                    break;

                case 'fr-ca':
                    localeUpdate = localUpdateFr();
                    break;

                case 'en-us':
                    localeUpdate = localUpdateEn();
                    break;

                default:
                // do not provide any localeUpdate and try moment use the default
            }

            if (localeUpdate) {
                moment.locale(locale, localeUpdate);
            } else {
                moment.locale(locale);
            }

        })
    ), { dispatch: false });


    fetchSkillSets$ = createEffect(() => this.actions$.pipe(
        ofType<FetchSkillSetsAction>(SESSION_SKILLSETS_FETCH),
        switchMap(() => {
            return this.skillSetsDataService.fetchSkillSets().pipe(
                map((res: any) => {
                    return new SetSkillSetsAction(res.skillsets);
                })
            );
        }),
        ossCatchError(([errorActions]) => from(errorActions))
    ));

    fetchCompanyFeatures$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchCompanyFeaturesAction>(SESSION_COMPANY_FEATURES_FETCH),
            withLatestFrom(this.store$.select(getCompany)),
            filter(([action, company]) => !!company),
            switchMap(([action, company]) => {

                return this.companyDataService
                    .fetchCompanyFeatures(company.id)
                    .pipe(
                        map((res: any) => {
                            return new SetCompanyFeaturesAction(res.features);
                        })
                    );
            }),
            ossCatchError(([errorActions]) => from(errorActions))
    ));

    fetchUserPermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType<FetchUserPermissionsAction>(SESSION_USER_PERMISSIONS_FETCH),
            map((action: FetchUserPermissionsAction) => action.payload),
            switchMap((userId: string) => {
                return this.userDataService.fetchUserPermissions(userId).pipe(
                    map((res: any) => {
                        return new SetUserPermissionsAction(
                            (res.permissions || []).map((permission) =>
                                Object.assign(
                                    new ConnectionPermission(),
                                    permission
                                )
                            )
                        );
                    })
                );
            }),
            ossCatchError(([errorActions]) => from(errorActions))
        )
    );
}
